import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useModalShell } from './useModalShell'
import RoomChoice from './RoomChoice'
import ConfirmAction from './ConfirmAction'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, X } from 'lucide-react'
import {
  cancelBooking,
  reinstateBooking,
  conflictHeadline,
  createBooking,
  defaultTeacher,
  fetchBookings,
  fetchHolds,
  fetchBookingLimits,
  fetchRepairableAbsences,
  fetchTechniqueClasses,
  fetchStaff,
  minutesOf,
  roomAvailability,
  timeFromMinutes,
  updateBooking,
  type BookedSlot,
  type BookingKind,
  type BookingRefusal,
} from '../api/bookings'
import { searchDancers, type DancerHit } from '../api/dancers'
import SchedulingAssistant from './SchedulingAssistant'
import { useSession } from '../useSession'
import { fetchDay, formatFullDate, formatTime, meridiem } from '../api/calendar'
import { techniqueLabel } from './techniqueLabel'

/*
 * **It used to stop at 90** (D-396).
 *
 * `asked`: *"we need to be able to book a room for longer than 90 minutes,
 * with Tonya's approval."* Nothing refused a longer booking — the picker
 * simply never offered one, so a two-hour rehearsal was un-askable and was
 * arranged by text, which is the thing this module exists to replace.
 *
 * Three hours is the ceiling because the studio's evening is 4:30 to 8:30; a
 * longer list would mostly be lengths no room has.
 */
const DURATIONS = [15, 30, 45, 60, 90, 120, 150, 180]

const KINDS: { value: BookingKind; label: string }[] = [
  { value: 'PrivateLesson', label: 'Private lesson' },
  { value: 'Choreography', label: 'Choreography' },
  { value: 'Rehearsal', label: 'Rehearsal' },
  { value: 'Other', label: 'Other' },
]

/**
 * Holds a room for a teacher.
 *
 * Opened by clicking an empty slot on the day grid, so the date and start time
 * arrive already answered. The room arrives as a suggestion rather than a
 * decision: a teacher asks "what is free at six?", so the picker answers that
 * instead of accepting a guess and refusing afterwards.
 *
 * Dancers are optional throughout. A teacher holding a slot with nobody in it
 * is the workbook's `SDT SPOT - ________` and a perfectly normal thing to
 * create (D-80); requiring a name here would make the common case impossible.
 */
/*
 * <b>One rendering of "this slot", for all three places that name it</b>
 * (D-428). The header, the room picker and the footer summary each had their
 * own copy, and they had already drifted: the header went on confidently
 * stating 8:00am after the other two had learned to admit that nobody had
 * picked a time yet. Found by a test, not by reading — the two correct copies
 * are what made the third look correct.
 */
function SlotTime({ start, end }: { start: number; end: number }) {
  return (
    <span className="tabular">
      {formatTime(timeFromMinutes(start))}
      {meridiem(timeFromMinutes(start))}&ndash;
      {formatTime(timeFromMinutes(end))}
      {meridiem(timeFromMinutes(end))}
    </span>
  )
}

/**
 * What is still to settle on a booking that was made anyway.
 *
 * The room is held in every one of these cases (D-163): refusing to hold it
 * would let a second teacher take the slot while the first waits, which is the
 * texting problem this module replaces.
 */
type HeldReason = 'length' | 'classTeacher' | 'family'

const HELD_REASONS: Record<HeldReason, string> = {
  length: 'Tonya has to agree the length. Anything longer than the usual limit goes to her.',
  classTeacher:
    "The routine’s teacher has to agree the lesson. A routine is made up with whoever set it.",
  // D-660. The reference belongs in the comment, never in the sentence.
  family: "The family has to agree to it. They say yes on their own Requests screen.",
}

export default function BookingForm({
  roomId: initialRoomId,
  date: initialDate,
  startMinutes: initialStart,
  startChosen = false,
  existing,
  onClose,
}: {
  roomId: string
  date: string
  startMinutes: number
  /*
   * **Whether that start is a choice or a fallback** (D-428). Opening from an
   * empty grid cell names a real time; the "Book a room" button has none to
   * name and used to borrow the studio's opening hour. That was invisible
   * while Starts was a select you could see; hiding the field turned it into
   * an 8am lesson nobody picked.
   *
   * **It defaults to false, so a caller must claim a start rather than
   * inherit one.** It shipped defaulting true and the teacher portal — the
   * screen a teacher actually books from — kept asserting a borrowed 4:30pm
   * she had never chosen, which is more dangerous than the 8:00am because it
   * is plausible enough to go unnoticed. A prop whose wrong value is invisible
   * belongs on the safe side of its own default; the cost of the safe side is
   * that she picks a time, which is the point of the screen.
   */
  startChosen?: boolean
  /** Present when amending rather than creating. */
  existing?: BookedSlot
  onClose: () => void
}) {
  const pressedBackdrop = useRef(false)
  const panel = useRef<HTMLFormElement>(null)

  const editing = existing !== undefined

  const [roomId, setRoomId] = useState(existing?.roomId ?? initialRoomId)

  /*
   * The day is editable, not fixed by where she tapped (D-318).
   *
   * Opened from a slot it is that slot's day and rarely changes. Opened from
   * the "Book a room" button it is whatever the calendar was anchored on - so
   * without this, booking Wednesday from the week view meant closing the form,
   * navigating, and starting again. "Find an open room on any given day" is the
   * question, and the day is half of it.
   */
  const [date, setDate] = useState(initialDate)
  /*
   * **Defaults to her when she teaches, and to nobody when she does not**
   * (D-373).
   *
   * Reported by the owner: *"When booking a room as a teacher it should default
   * to their name."* It opened on *"Choose a teacher"* over every member of
   * staff, and a teacher booking her own room had to find herself in it.
   *
   * **Gated on `teaches`, not on merely holding a `staffId`.** Tonya's owner
   * account is created with `UserAccount.ForStaff`, so she **has** a staff link
   * and a `staffId` — an earlier cut of this defaulted the field to *her*,
   * which is wrong on every booking she makes, because she books on other
   * people's behalf. `teaches` is exactly "has days here" (D-322), which she
   * does not, so she keeps the placeholder and chooses deliberately. The
   * `required` on the field is what used to force that choice from everybody;
   * a default that fills it for the wrong person is how a booking gets quietly
   * attributed.
   *
   * **Derived, not seeded into `useState`.** The session is a query and is
   * `undefined` on the first render, so an initialiser reading it captures the
   * empty string once and never revisits — the field would open exactly as it
   * did before. Found by the test, not by reading. `null` means *she has not
   * chosen*, which lets a late default arrive and still lose to a real choice.
   */
  const { session } = useSession()
  const [chosenStaffId, setChosenStaffId] = useState<string | null>(existing?.staffId ?? null)
  const staffId = chosenStaffId ?? defaultTeacher(session)
  const setStaffId = setChosenStaffId
  const [startMinutes, setStartMinutes] = useState(
    existing ? minutesOf(existing.startTime) : initialStart,
  )
  const [startPicked, setStartPicked] = useState(startChosen || Boolean(existing))
  const [duration, setDuration] = useState(
    existing ? minutesOf(existing.endTime) - minutesOf(existing.startTime) : 30,
  )
  const [kind, setKind] = useState<BookingKind>(existing?.kind ?? 'PrivateLesson')
  const [cadence, setCadence] = useState<'Once' | 'Weekly' | 'Biweekly'>('Once')
  const [repairs, setRepairs] = useState('')
  const [isMakeup, setIsMakeup] = useState(false)
  // The technique class a makeup stands in for (D-729); blank means a routine.
  const [techniqueClassId, setTechniqueClassId] = useState('')
  /*
   * **Typing the day and time is the escape hatch, not the path** (D-428).
   *
   * The matrix is how a lesson gets booked now, so the two fields it fills sit
   * behind a link. They cannot go entirely: it covers a fortnight, a room hold
   * with no dancer has no matrix at all, and the feed can refuse for a teacher
   * a family may not ask about. Open by default when editing, because then
   * there is a time already set and no matrix showing.
   */
  const [byHand, setByHand] = useState(editing)
  const [repeatUntil, setRepeatUntil] = useState('')
  const [notes, setNotes] = useState(existing?.notes ?? '')
  // The studio's own half (D-656). A family never sees this one.
  const [studioNotes, setStudioNotes] = useState(existing?.studioNotes ?? '')

  const [dancerQuery, setDancerQuery] = useState('')
  const [dancers, setDancers] = useState<DancerHit[]>(
    // Names and ids arrive in the same order from the server, so the roster can
    // be rebuilt without a second lookup.
    (existing?.dancerIds ?? []).map((id, i) => ({
      dancerId: id,
      name: existing!.dancerNames[i] ?? 'Dancer',
      ageYears: 0,
      household: null,
      householdId: null,
    })),
  )

  const [refusal, setRefusal] = useState<BookingRefusal | null>(null)

  /*
   * Held, but not settled. Only ever set after a successful save.
   *
   * <b>Which question is outstanding, not merely that one is</b> (D-376,
   * D-509). It was a boolean meaning "Tonya must agree the length"; there are
   * two independent gates now and they can both be shut at once, so a boolean
   * would have shown the wrong sentence for the new one and no sentence at all
   * when both applied. Naming them is also what stops the next gate quietly
   * inheriting this one's copy.
   */
  const [held, setHeld] = useState<HeldReason[]>([])

  const queryClient = useQueryClient()

  const { data: staff } = useQuery({ queryKey: ['staff'], queryFn: fetchStaff })

  /*
   * Where the automatic length stops (D-396). Undefined while it loads and
   * after a failure, and `needsApproval` is false in both — the picker offers
   * the long lengths regardless, and the server still holds and flags them
   * (D-163). A missing label is a smaller fault than a wrong one.
   */
  const { data: limits } = useQuery({ queryKey: ['booking-limits'], queryFn: fetchBookingLimits })
  const maxAutomatic = limits?.maxDurationMinutes

  const { data: dancerHits } = useQuery({
    queryKey: ['dancer-search', dancerQuery],
    queryFn: () => searchDancers(dancerQuery),
    enabled: dancerQuery.trim().length >= 2,
  })

  /*
   * **Saying what a private makes up for** (D-406).
   *
   * A teacher and a family settle a private by text (D-287) and she books the
   * room herself — so before this, the only path that could ever say a private
   * repaired something was the parent's own ask. The absence stayed open, the
   * lesson happened, and the percentage never moved.
   *
   * **The same three conditions the server enforces**, because a control that
   * appears where the server would refuse it is worse than no control: one
   * private lesson, exactly one dancer, and not a repeating series.
   */
  /*
   * **Choosing the absence needs one dancer; saying it is a makeup does not**
   * (D-421, D-422).
   *
   * The select files this booking against one named absence, so it asks for
   * exactly one dancer and reads her open ones. The tick says only *that* this
   * is a makeup — a duet or trio marks together and each family answers for
   * her own, so it allows up to three and needs no lookup at all.
   */
  const couldBeMakeup =
    !editing
    && (kind === 'PrivateLesson' || kind === 'Choreography')
    && cadence === 'Once'
    && dancers.length >= 1
    && dancers.length <= 3

  const canRepair = couldBeMakeup && kind === 'PrivateLesson' && dancers.length === 1

  /*
   * Her competitive technique classes on the day (D-729). Most dancers have
   * none, and then the form never asks what kind of makeup this is. Not when
   * editing: the lesson's class is settled, and the server holds a technique
   * makeup to its rules on the edit itself.
   */
  const { data: techniqueClasses } = useQuery({
    queryKey: ['technique-classes', dancers[0]?.dancerId, date],
    queryFn: () => fetchTechniqueClasses(dancers[0]!.dancerId, date),
    enabled: canRepair && !editing,
  })
  const techniqueOffered = canRepair && !editing && (techniqueClasses ?? []).length > 0
  const technique = techniqueOffered && techniqueClassId !== ''
  const techniqueStyle = (techniqueClasses ?? []).find((c) => c.classId === techniqueClassId)?.style ?? ''

  const { data: repairable } = useQuery({
    // Filtered to the style only while its chip is on screen, or a chip the
    // day no longer offers would narrow the routine list (found by the audit).
    queryKey: ['repairable-absences', dancers[0]?.dancerId, technique ? techniqueClassId : null],
    queryFn: () => fetchRepairableAbsences(dancers[0]!.dancerId, technique ? techniqueClassId : null),
    enabled: canRepair,
  })

  /*
   * **One value answers "is she being asked which absence" — for the control
   * and for the request** (D-428). They were two expressions that agreed by
   * inspection and drifted the moment either moved: `repairable` refetching to
   * empty for the same dancer left the panel promising her family would be
   * asked while the request still carried the id she had picked. A narrow
   * window, and the server refuses it — but two copies of one question is what
   * makes it come back.
   */
  // A technique makeup always has something to say - bank it, or name a class
  // - so its select is on screen even when she has no absence of that style.
  const namingAbsence = canRepair && (technique || (repairable ?? []).length > 0)

  /*
   * A dancer swapped out takes her absence with her. Without this, picking
   * Ellie, choosing her solo, then replacing her with Maya would file Maya's
   * private against Ellie's absence — which the server refuses, but only after
   * the teacher has filled the whole form in.
   */
  useEffect(() => {
    setRepairs('')
    // And the day: her technique classes are the day's, so a chip pressed for
    // Thursday means nothing on Monday (found by the audit).
    setTechniqueClassId('')
  }, [dancers[0]?.dancerId, kind, date])

  /*
   * Escape, the focus trap and the scroll lock come from the shared shell
   * (D-515). Its own listener did not stop the key, and it never trapped Tab.
   */
  useModalShell({ panel, onDismiss: onClose })

  const endMinutes = startMinutes + duration

  /*
   * Both are needed: a room busy with a class is no more available than one
   * busy with a private.
   *
   * **`wholeStudio`, because "what is free?" is a question about the building**
   * (D-368). Scoped to her own classes this grid could not see another
   * teacher's class in a room, so it reported the room free — and then the
   * server, which checks occurrences unscoped, refused. The screen disagreed
   * with the server and offered to override it.
   *
   * It is in the query key as well as the call: keyed on `{}` while the request
   * carried the flag, a scoped week already in the cache would be served to
   * this grid and the fix would appear not to work.
   */
  const roomView = { wholeStudio: true }
  const { data: day } = useQuery({
    queryKey: ['calendar', 'day', date, roomView],
    queryFn: () => fetchDay(date, roomView),
  })
  const { data: dayBookings } = useQuery({ queryKey: ['bookings', date], queryFn: () => fetchBookings(date, date) })
  const { data: dayHolds } = useQuery({ queryKey: ['bookings', 'holds', date], queryFn: () => fetchHolds(date, date) })

  /*
   * `existing?.id` so the booking being edited does not clash with itself
   * (D-396). Without it, opening a private reported its own room as taken —
   * true, by the thing on screen, and read by the person editing it as a real
   * conflict.
   */
  const availability = roomAvailability(
    day?.rooms ?? [],
    dayBookings ?? [],
    startMinutes,
    endMinutes,
    existing?.id,
    dayHolds ?? [],
    // A hold is free for the teacher it is held for (D-710).
    staffId,
  )
  const chosen = availability.find((r) => r.roomId === roomId)

  /** Everything showing this day is stale once a booking moves. */
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['bookings'] })
    queryClient.invalidateQueries({ queryKey: ['conflicts'] })
  }

  const save = useMutation({
    mutationFn: (allowConflicts: boolean) => {
      const common = {
        roomId,
        staffId,
        date,
        startTime: timeFromMinutes(startMinutes),
        endTime: timeFromMinutes(endMinutes),
        kind,
        dancerIds: dancers.map((d) => d.dancerId),
        notes: notes.trim() || null,
        studioNotes: studioNotes.trim() || null,
        allowConflicts,
      }

      // An edit changes this booking only, never its series — amending every
      // future week from one screen is a different and far more surprising act.
      return editing
        ? updateBooking(existing!.id, common)
        : createBooking({
            ...common,
            cadence,
            repeatUntil: cadence === 'Once' ? null : repeatUntil || null,
            /*
              Only when the control is actually on screen (D-406). The server
              refuses a repair on a rehearsal, on a series and on anything but
              one dancer — so sending a stale id from a form she has since
              changed would turn a valid booking into a 400 about a field she
              can no longer see.
            */
            repairsOccurrenceId: namingAbsence ? repairs || null : null,
            // Only while the chip is on screen, for the same reason (D-729).
            techniqueClassId: technique ? techniqueClassId : null,
            // Only when she has not named one: a named absence is the
            // stronger statement, and sending both would be two answers.
            /*
             * <b>The send guard mirrors the render guard, or the screen lies</b>
             * (D-428). `repairs` outlives the select that set it: choose Ellie's
             * solo, then add Maya, and the duet unmounts the select while the id
             * stays in state. A bare `!repairs` then read false, so the panel
             * promised both families would be asked and the server was told this
             * was an ordinary lesson — no claim written, nobody asked, the
             * entitlement quietly unspent. Adding a dancer or switching to
             * Choreography both reach it. Whether an absence was named is only
             * a question while one can be.
             */
            // Sent for a banked technique lesson too: the server asks for it, and
            // raises no family claim beside a technique class (found by the audit).
            isMakeup: couldBeMakeup && isMakeup && (namingAbsence ? !repairs : true),
          })
    },
    onSuccess: (result) => {
      if (!result.ok) {
        setRefusal(result.refusal)
        return
      }

      refresh()

      /*
       * **An over-limit booking says so before it closes** (D-376).
       *
       * Owner: *"When a booking is too long maybe have something pop up that
       * says it is pending approval."* The room **is** held either way — that
       * is D-163's decision and it is unchanged — but the length still needs
       * Tonya, and the dialog closing silently is how a teacher learns that
       * only when she is asked about it. An ordinary booking still closes with
       * no ceremony, because it needs none.
       */
      /*
       * **The same silence, one gate over** (D-509). A private repairing a
       * routine, booked with anybody but that routine's teacher, is held until
       * she agrees — and this dialog closed on it exactly as D-376 describes
       * above, so the teacher left believing it was booked and then got an
       * email agreeing to a question she never knew had been asked.
       */
      const gates: HeldReason[] = []

      if ('awaitingApproval' in result && result.awaitingApproval) gates.push('length')
      if ('awaitingClassTeacher' in result && result.awaitingClassTeacher) gates.push('classTeacher')
      if ('awaitingFamily' in result && result.awaitingFamily) gates.push('family')

      if (gates.length > 0) {
        setHeld(gates)
        return
      }

      onClose()
    },
  })

  /*
   * The teacher's cancel fired on one tap too, and it mails every family on
   * the booking (D-494). Same dialog as the portal's, for the same reason.
   */
  const [confirmingCancel, setConfirmingCancel] = useState(false)

  /*
   * Undo (D-648). Its error is shown rather than swallowed: the two ways it
   * legitimately fails - the room taken, or the lesson having repaired a missed
   * class - are both things she has to know rather than retry.
   */
  const reinstate = useMutation({
    mutationFn: () => reinstateBooking(existing!.id),
    onSuccess: () => {
      refresh()
      onClose()
    },
  })

  const cancel = useMutation({
    mutationFn: () => cancelBooking(existing!.id),
    onSuccess: () => {
      refresh()
      onClose()
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!staffId) return
    setRefusal(null)
    save.mutate(false)
  }

  const field =
    'w-full rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13.5px] outline-none focus:border-accent focus:bg-surface'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-5"
      /*
        The press is remembered as well as the click (D-515). A click is
        dispatched at the nearest common ancestor of its press and release, so
        selecting text in the repairs note and overshooting onto the dim area
        retargets the click here — and this is the costliest dialog in the app
        to lose that way: a room, a date, a duration, the dancers, the makeup
        tick and the note, all gone with no confirmation.
      */
      onMouseDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pressedBackdrop.current) onClose()
      }}
    >
      <form
        ref={panel}
        tabIndex={-1}
        /*
          It says it is a dialog and it says what it is (D-515). It had none of
          the three, so an assistive technology read it as part of the schedule
          behind it — the same defect this decision records on `MediaPicker`,
          on the app's largest form.

          <b>Pointed at the heading rather than restating it</b>, because the
          heading is conditional and a literal name is not: a first cut said
          "Hold a room" over a panel visibly titled *Edit booking*, announcing a
          new hold on a booking she was amending. A name that disagrees with its
          own heading is worse than no name, and binding the two makes the
          disagreement unrepresentable.
        */
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-heading"
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        /*
          **Wider once the assistant is in it** (D-426). 28rem was right for a
          column of fields; the availability grid is a fortnight of rows with a
          time axis across, and at that width it was a cramped strip inside a
          scrolling dialog — reported from a screenshot.

          `w-full` with a max rather than a fixed width, because a flex child
          with an auto margin and a max-width shrink-wraps and never reaches it
          (D-255). It still fits a phone: the max only bites above ~46rem.
        */
        className="max-h-[86vh] w-full max-w-[46rem] overflow-y-auto rounded-2xl border border-line bg-surface shadow-2xl"
      >
        <header
          className="room-tint flex items-start gap-3 border-b border-line px-5 py-4"
          style={
            {
              '--room-color': chosen?.color ?? 'var(--color-accent)',
              borderLeft: '4px solid var(--room-display)',
            } as React.CSSProperties
          }
        >
          <div className="min-w-0 flex-1">
            <h2 id="booking-heading" className="font-display text-lg">{editing ? 'Edit booking' : 'Hold a room'}</h2>
            <p className="mt-0.5 text-[12.5px] text-ink-3">
              {formatFullDate(date)}
              {startPicked ? (
                <>
                  {' '}
                  &middot; <SlotTime start={startMinutes} end={endMinutes} />
                </>
              ) : null}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-7 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-surface-2 hover:text-ink"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </header>

        <div className="flex flex-col gap-4 px-5 py-4">
          {/*
            **Held, and waiting on Tonya** (D-376). The room is reserved either
            way (D-163) — this only says the *length* is not settled, which is
            the thing she would otherwise learn when she was asked about it.
          */}
          {held.length > 0 && (
            <div
              role="status"
              className="rounded-lg border border-warn bg-warn-bg px-3.5 py-3 text-[13px]"
            >
              {/*
                <b>Every shut gate is named</b> (D-509, D-660). They are
                independent questions with different answerers, so "it needs
                Tonya" on a booking that also needs the routine's teacher would
                send her away having answered half of it.

                <b>A list rather than a sentence per combination.</b> With three
                gates the prose was eight sentences to write and eight to keep
                true, and the fourth would have been sixteen — which is how a
                screen ends up quietly naming the wrong one.
              */}
              <p className="font-semibold">
                {held.length === 1
                  ? 'The room is held, and one thing is still to settle.'
                  : `The room is held, and ${held.length} things are still to settle.`}
              </p>

              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-ink-2">
                {held.map((why) => (
                  <li key={why}>{HELD_REASONS[why]}</li>
                ))}
              </ul>

              <p className="mt-1.5 text-ink-2">Nobody else can take the room meanwhile.</p>

              <button
                type="button"
                onClick={onClose}
                className="mt-2.5 rounded-md border border-line-2 bg-surface px-3 py-1.5 text-[12.5px] font-semibold"
              >
                Done
              </button>
            </div>
          )}

          {/*
            **Held is an answer, so the questions go away** (D-388).

            Reported: *"a text box should come up with this room is held
            message and not keep this book a room view open."* The
            confirmation used to sit at the top of a form that was still
            asking for a room, a teacher and a time — all of it already
            submitted and none of it still editable. It read as a warning
            attached to a live form rather than as the booking being done.

            The panel above stays where it is and everything below it goes,
            so what is left is the sentence and the way out. The footer is
            hidden with it — a *Hold the slot* button under a slot already
            held is a control that would do nothing.
          */}
          {held.length === 0 && (
            <>
          {refusal && (
            <div role="alert" className="rounded-lg border border-crit bg-crit-bg px-3.5 py-3 text-[13px] text-crit">
              <p className="flex items-center gap-1.5 font-semibold">
                <AlertTriangle size={14} strokeWidth={2.5} />
                {/*
                  **What kind of thing is in the way** (D-376, completing
                  D-368). The 409 body has carried `kind` per conflict all
                  along and the screen rendered only `description`, whose
                  sentence names the *person* but never says whether the clash
                  is the room, the teacher or a dancer. With the grid now
                  honest about rooms (D-368), the refusal a teacher actually
                  meets is *herself* — and "collides with something already
                  scheduled" over a room the grid just called free is the
                  sentence that produced the original report.
                */}
                {conflictHeadline(refusal)}
              </p>

              {refusal.conflicts.length > 0 && (
                <ul className="mt-1.5 flex flex-col gap-1">
                  {refusal.conflicts.map((c, i) => (
                    <li key={i}>{c.description}</li>
                  ))}
                </ul>
              )}

              {/*
                An override, not a retry. Tonya may know something the schedule
                does not, but it has to be a separate deliberate act — and the
                conflict is still recorded afterwards rather than hidden.
              */}
              {refusal.reason === 'conflict' && (
                <button
                  type="button"
                  onClick={() => save.mutate(true)}
                  className="mt-2 rounded-md border border-crit px-2.5 py-1 text-[12.5px] font-semibold"
                >
                  Book it anyway
                </button>
              )}
            </div>
          )}

          <label className="block">
            <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">Teacher</span>
            <select value={staffId} onChange={(e) => setStaffId(e.target.value)} required className={field}>
              <option value="">Choose a teacher</option>
              {staff?.map((s) => (
                <option key={s.staffId} value={s.staffId}>
                  {s.name}
                  {s.title ? ` - ${s.title}` : ''}
                </option>
              ))}
            </select>
          </label>

          {/* Dancers, optional. An empty slot is the point of the feature. */}
          <div>
            <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">
              Dancers <span className="font-normal text-ink-3">- optional</span>
            </span>

            {dancers.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {dancers.map((d) => (
                  <button
                    key={d.dancerId}
                    type="button"
                    onClick={() => setDancers((cur) => cur.filter((x) => x.dancerId !== d.dancerId))}
                    className="flex items-center gap-1 rounded-full border border-accent bg-accent-soft px-2.5 py-1 text-[12px] font-semibold text-accent-2"
                  >
                    {d.name}
                    <X size={11} strokeWidth={3} />
                  </button>
                ))}
              </div>
            )}

            <input
              type="search"
              value={dancerQuery}
              onChange={(e) => setDancerQuery(e.target.value)}
              placeholder="Search to add a dancer"
              className={field}
            />

            {dancerHits && dancerHits.length > 0 && dancerQuery.trim().length >= 2 && (
              <ul className="mt-1.5 max-h-36 overflow-y-auto rounded-lg border border-line">
                {dancerHits
                  .filter((h) => !dancers.some((d) => d.dancerId === h.dancerId))
                  .map((hit) => (
                    <li key={hit.dancerId}>
                      <button
                        type="button"
                        onClick={() => {
                          setDancers((cur) => [...cur, hit])
                          setDancerQuery('')
                        }}
                        className="flex w-full items-baseline gap-2 px-3 py-2 text-left text-[13px] hover:bg-surface-2"
                      >
                        <span className="flex-1 truncate font-semibold">{hit.name}</span>
                        <span className="tabular shrink-0 text-[11.5px] text-ink-3">{hit.ageYears}</span>
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {/*
            The day, beside the time rather than fixed by where she tapped
            (D-318). Changing it re-runs the availability queries above, so the
            room list answers "what is free on *that* day at this time".
          */}
          {/*
            **Not rendered rather than hidden** (D-428). A `hidden` class is a
            stylesheet away from being visible, and jsdom has no stylesheet — so
            the field was still found by label, still changed by a test, and the
            assertion that it was out of the way could not fail. Unmounted, it
            is out of reach of a test and of a screen reader for the same reason.
          */}
          {byHand && (
          <label className="mb-3 block">
            <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">Day</span>
            <input
              type="date"
              value={date}
              onChange={(e) => {
                if (!e.target.value) return
                setDate(e.target.value)

                // A room that was free on Monday is not necessarily free on
                // Wednesday, and carrying the choice over would show a chosen
                // room the picker is simultaneously reporting as taken.
                setRoomId('')
              }}
              className={field}
            />
          </label>
          )}

          {/*
            **Wraps, because three selects do not fit a phone.** At 390px each
            got about 110px and the values were clipped to "30 minu" and
            "Private l" — a control whose value you cannot read. Only visible
            once the form could be opened in a browser at all (D-427); the
            basis is a minimum width rather than a breakpoint, so it wraps when
            it has to and stays one row when it fits.
          */}
          <div className="flex flex-wrap gap-3">
            {byHand && (
            <label className="min-w-[9rem] flex-1">
              <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">Starts</span>
              <select
                value={startMinutes}
                onChange={(e) => {
                  setStartMinutes(Number(e.target.value))
                  setStartPicked(true)
                }}
                className={field}
              >
                {Array.from({ length: 57 }, (_, i) => 8 * 60 + i * 15).map((m) => (
                  <option key={m} value={m}>
                    {formatTime(timeFromMinutes(m))}
                    {meridiem(timeFromMinutes(m))}
                  </option>
                ))}
              </select>
            </label>
            )}

            <label className="min-w-[9rem] flex-1">
              <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">Length</span>
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                /*
                  Described by the note, not named by it. The note lives inside
                  the `<label>`, so without this a screen reader announces the
                  field as "Length Over 60 minutes, so Tonya confirms…" — the
                  whole sentence read back as the control's name.
                */
                aria-describedby={
                  maxAutomatic !== undefined && duration > maxAutomatic
                    ? 'booking-length-note'
                    : undefined
                }
                className={field}
              >
                {DURATIONS.map((d) => (
                  <option key={d} value={d}>
                    {d} minutes
                    {maxAutomatic !== undefined && d > maxAutomatic ? ' — needs Tonya' : ''}
                  </option>
                ))}
              </select>
              {/*
                **Said before the choice, not after it** (D-396). The form used
                to learn this from the save and report it in a banner, so the
                only way to find out a length needed her was to make the
                booking. The room is held either way (D-163) — what waits is
                the length.
              */}
              {maxAutomatic !== undefined && duration > maxAutomatic && (
                <p id="booking-length-note" className="mt-1.5 text-[12px] text-ink-2">
                  Over {maxAutomatic} minutes, so Tonya confirms the length. The room is held
                  meanwhile.
                </p>
              )}
            </label>

            <label className="min-w-[9rem] flex-1">
              <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">Type</span>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as BookingKind)}
                className={field}
              >
                {KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/*
            **When everyone on this booking is free** (D-419, D-420).

            Only once a dancer is on it: a room hold has nobody to be free
            *with*, and the feed refuses a call with no dancers. So this appears
            exactly when the question has an answer, which is also when a
            teacher is arranging a lesson rather than reserving a room (D-80).

            She already has the studio picture on the calendar; what she does
            not have is the dancer's half of it, which is the whole reason
            arranging a private takes three text messages.
          */}
          {dancers.length > 0 && dancers.length <= 3 && staffId && (
            <div className="border-t border-line pt-3.5">
              <SchedulingAssistant
                dancerIds={dancers.map((d) => d.dancerId)}
                teacherStaffId={staffId}
                minutes={duration}
                /*
                 * <b>`timeFromMinutes` already ends in `:00`.</b> Appending
                 * another gave `"18:00:00:00"`, compared with `===` against the
                 * feed's `"18:00:00"` — so the block she had just tapped was
                 * never marked as the one she picked, on any day since this
                 * shipped. `AvailabilityGrid`'s own test passes a clean string,
                 * which is what let the component be right and the wiring wrong.
                 *
                 * Gated on `startPicked`, because "the one you picked" is not a
                 * thing to say when nobody has picked (D-428).
                 */
                picked={startPicked ? { date, start: timeFromMinutes(startMinutes) } : undefined}
                teacherLabel="Teacher"
                onPick={(pickedDate, pickedStart) => {
                  setDate(pickedDate)
                  setStartMinutes(minutesOf(pickedStart))
                  setStartPicked(true)
                }}
              />
            </div>
          )}



          {/*
            The room is a choice, not a guess. The form used to accept any room
            and refuse afterwards with a conflict, which made the teacher work
            out what they had hit. Free rooms are offered; busy ones say what is
            in them and cannot be picked.

            **Last, because what is free depends on everything above it**
            (D-388). It used to come first, so the availability shown was for
            whatever day and time the form happened to open with, and picking a
            room before saying when meant the answer could stop being true
            underneath her — which is why changing the day had to silently clear
            the choice. Asked for from testing in exactly this order: "have
            teacher first, then day, then time, then room."
          */}
          <div>
            {/*
              **Says which slot it is answering about** (D-428). It sits under
              the grid you tapped, so naming the time turns it from a list of
              rooms into the answer to "and where" — and on a phone the slot you
              chose has usually scrolled out of sight by now.
            */}
            <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">
              {/*
                The section still says what it is. Replacing the label outright
                with the slot line left the room chips unnamed — which broke the
                order check, and would have left a reader working out what the
                row of pills was for.
              */}
              <span className="mb-0.5 block text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
                Room
              </span>
              {startPicked ? (
                <>
                  {formatFullDate(date)}, <SlotTime start={startMinutes} end={endMinutes} />
                </>
              ) : (
                <span className="font-normal text-ink-3">Pick a time above first</span>
              )}
              <span className="mt-0.5 block font-normal text-ink-3">
                {availability.filter((r) => r.isFree).length} of {availability.length} rooms free then
              </span>
            </span>

            {/*
              The chips themselves live in RoomChoice now, so the move panel
              shows the same thing (D-897). Availability is still worked out
              here, from the slot this form is already drawing a grid for.
            */}
            <RoomChoice options={availability} roomId={roomId} onPick={setRoomId} />

            {chosen && !chosen.isFree && (
              <p className="mt-1.5 text-[12px] font-semibold text-crit">
                {chosen.name} is taken for this slot — pick another or change the time.
              </p>
            )}

            {/*
              **Nothing free is a dead end unless it says what to do** (D-388).

              The line above only appears once a room is *chosen*, which cannot
              happen when every one is disabled — so a teacher picking a busy
              slot met six greyed cards, an accurate "0 free at this time", and
              no next step. Seen by opening the dialog on a real class night
              rather than by reading it: at 4:30pm on 8 September all six rooms
              are genuinely taken.

              It names the controls above rather than saying "try again",
              because they are the two things that change the answer and they
              are now both on screen before this.
            */}
            {!chosen && availability.length > 0 && availability.every((r) => !r.isFree) && (
              <p className="mt-1.5 text-[12px] font-semibold text-crit">
                Every room is taken then. Change the day or the time above and these will update.
              </p>
            )}
          </div>


          {/*
            **One question with a follow-up** (D-428).

            This was two controls hiding each other: a select naming the
            absence, and a tick saying the family would choose. Reported as
            confusing, and it was — they are the same question asked twice.

            The tick now asks whether it is a makeup at all; the follow-up asks
            *which*, with "let them choose" as the answer when she does not
            know. That refines D-422 rather than reversing it: the family still
            decides when nobody says otherwise, and a teacher who already knows
            is no longer made to wait for a parent to confirm what she told
            them.
          */}
          {couldBeMakeup && (
            <div>
              <button
                type="button"
                role="switch"
                aria-checked={isMakeup}
                onClick={() => {
                  // Untick and the follow-up goes with it, or an absence she
                  // has changed her mind about would still be filed.
                  if (isMakeup) setRepairs('')
                  setIsMakeup((v) => !v)
                }}
                className={`flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left ${
                  isMakeup ? 'border-accent bg-accent-soft' : 'border-line-2 bg-surface'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`mt-px grid size-[18px] shrink-0 place-items-center rounded-[5px] border ${
                    isMakeup ? 'border-accent bg-accent text-on-accent' : 'border-line-2'
                  }`}
                >
                  {isMakeup && <Check size={12} strokeWidth={3.5} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-semibold">This lesson is a makeup</span>
                  <span className="mt-0.5 block text-[12.5px] text-ink-2">
                    Tick it when the lesson replaces a class they missed.
                  </span>
                </span>
              </button>

              {isMakeup && (
                <div className="mt-2">
                  {/*
                    **What kind of makeup** (D-729). A routine is the old answer
                    and stays the default; a technique chip names the class the
                    lesson stands in for, and `Bank it` is then the first option
                    below, because a technique makeup is not left to the family.
                  */}
                  {techniqueOffered && (
                    <div className="mb-2.5">
                      <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">
                        What does it make up?
                      </span>
                      <div className="flex flex-wrap gap-1.5" role="group" aria-label="What this lesson makes up">
                        <Chip
                          on={!technique}
                          onClick={() => {
                            setTechniqueClassId('')
                            setRepairs('')
                          }}
                        >
                          A routine
                        </Chip>
                        {(techniqueClasses ?? []).map((c) => (
                          <Chip
                            key={c.classId}
                            on={techniqueClassId === c.classId}
                            onClick={() => {
                              setTechniqueClassId(c.classId)
                              setRepairs('')
                            }}
                          >
                            {techniqueLabel(c, techniqueClasses ?? [])} technique
                          </Chip>
                        ))}
                      </div>
                    </div>
                  )}
                  {namingAbsence ? (
                    <label className="block">
                      <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">
                        Which class?{' '}
                        <span className="font-normal text-ink-3">
                          {technique ? '- or bank it' : '- or leave it to them'}
                        </span>
                      </span>
                      <select
                        value={repairs}
                        onChange={(e) => setRepairs(e.target.value)}
                        aria-label="Which class this lesson makes up"
                        className={field}
                      >
                        <option value="">{technique ? 'Bank it' : 'Let them choose'}</option>
                        {(repairable ?? []).map((r) => (
                          <option key={r.occurrenceId} value={r.occurrenceId}>
                            {r.className} &middot; {formatFullDate(r.date)}
                          </option>
                        ))}
                      </select>
                      <span className="mt-1 block text-[11.5px] text-ink-3">
                        {repairs
                          ? `Marks ${technique ? 'her' : 'them'} present for that class, once you take attendance.`
                          : technique
                            ? `Once you mark her present, it covers her oldest missed ${techniqueStyle} class, now or later.`
                            : 'It waits in their portal, and repairs nothing until they choose.'}
                      </span>
                    </label>
                  ) : (
                    /*
                      Nothing to offer — a duet or trio, or a dancer with no
                      open absence. Saying so beats an empty select, and the
                      family can still answer it.
                    */
                    <p
                      role="status"
                      className="rounded-lg border border-warn bg-warn-bg px-3 py-2.5 text-[12.5px]"
                    >
                      They choose which class it makes up. The room is held either way &mdash;
                      but until they pick, it repairs nothing and attendance is unchanged.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/*
            Recurrence is a creation-time choice. Changing one booking's pattern
            after the fact would have to decide what happens to the weeks already
            created, which is a bigger question than an edit dialog should ask.
          */}
          <div className={`flex gap-3 ${editing ? 'hidden' : ''}`}>
            <label className="flex-1">
              <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">Repeats</span>
              <select
                value={cadence}
                onChange={(e) => setCadence(e.target.value as typeof cadence)}
                className={field}
              >
                <option value="Once">Just this once</option>
                <option value="Weekly">Every week</option>
                <option value="Biweekly">Every other week</option>
              </select>
            </label>

            {cadence !== 'Once' && (
              <label className="flex-1">
                <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">Until</span>
                <input
                  type="date"
                  value={repeatUntil}
                  min={date}
                  onChange={(e) => setRepeatUntil(e.target.value)}
                  required
                  className={field}
                />
              </label>
            )}
          </div>

          {/*
            **Two notes, labelled by who reads them** (D-656). `asked`: *"the
            same two-note split when booking a room."*

            **The existing box stays the family's**, because it already was:
            the portal has always put it on the parent's upcoming list, so
            relabelling it as private would have hidden something families rely
            on. That is the opposite of the attendance case, where the note had
            never been shown and the *family* half is what had to be added.
          */}
          <label className="block">
            <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">
              Note for the family <span className="font-normal text-ink-3">- optional</span>
            </span>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="They can read this on their schedule"
              className={field}
            />
          </label>

          <label className="mt-3 block">
            <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">
              Studio note <span className="font-normal text-ink-3">- optional, staff only</span>
            </span>
            <input
              type="text"
              value={studioNotes}
              onChange={(e) => setStudioNotes(e.target.value)}
              placeholder="The family never sees this"
              className={field}
            />
          </label>
            </>
          )}
        </div>

        {held.length === 0 && (
          <>
        {/*
          What the matrix chose, restated where the decision is made. Without
          this the fields are hidden and nothing on screen says when the lesson
          is -- which is worse than the fields were.
        */}
        <div className="border-t border-line bg-surface-2 px-5 pt-2.5 text-[12px] text-ink-2">
          {startPicked ? (
            <>
              <span className="font-semibold text-ink">{formatFullDate(date)}</span>,{' '}
              <SlotTime start={startMinutes} end={endMinutes} />
              {chosen ? (
            <>
              {' '}
              &middot; {chosen.name}
            </>
          ) : null}
            </>
          ) : (
            <span className="font-semibold text-ink">No time picked yet</span>
          )}
          {!byHand && (
            <>
              {` `}
              &middot;{` `}
              <button
                type="button"
                /*
                 * <b>Opening the fields is itself picking the time they show.</b>
                 * Otherwise the one value the select is already displaying is
                 * the one she cannot choose: re-selecting the selected option
                 * fires no `change` in a browser, so the submit stayed disabled
                 * for exactly 8:00am and she had to pick 8:15 and come back.
                 * Revealing a field showing a time <i>is</i> a deliberate act,
                 * and the summary line beside this restates what she gets.
                 */
                onClick={() => {
                  setByHand(true)
                  setStartPicked(true)
                }}
                className="font-semibold text-accent underline"
              >
                enter by hand
              </button>
            </>
          )}
        </div>

        {confirmingCancel && (
          <ConfirmAction
            title="Cancel this booking?"
            body={
              existing
                ? `${existing.sizeLabel} on ${existing.date} at ${existing.startTime.slice(0, 5)}. ` +
                  (existing.dancerNames.length > 0
                    ? `${existing.dancerNames.join(", ")} will be told.`
                    : "The room goes back to whoever wants it.")
                : "This booking will be called off."
            }
            confirmLabel="Yes, cancel it"
            busyLabel="Cancelling..."
            busy={cancel.isPending}
            onDismiss={() => setConfirmingCancel(false)}
            onConfirm={() => {
              setConfirmingCancel(false)
              cancel.mutate()
            }}
          />
        )}

        <footer className="flex items-center gap-2 border-t border-line px-5 py-3.5">
          {/*
            Cancelling keeps the row and frees the room (A-12) rather than
            deleting it, so a slot that was given up stays visible in history.
          */}
          {/*
            <b>Undo, where cancel would be</b> (D-648). A booking that is already
            off has nothing to cancel, and offering it anyway is a control that
            cannot do anything - so the same place carries the opposite action.

            No confirmation. Cancelling is the destructive half and asks first;
            putting one back is what somebody does having just realised, and a
            dialog between her and the fix is the wrong friction. The server
            refuses if the room has gone in the meantime, and that refusal is
            the thing worth showing.
          */}
          {editing && existing?.status === 'Cancelled' && (
            <button
              type="button"
              onClick={() => reinstate.mutate()}
              disabled={reinstate.isPending}
              className="rounded-lg border border-accent px-3 py-2 text-[13px] font-semibold text-accent-2 transition-colors hover:bg-accent-soft disabled:opacity-60"
            >
              {reinstate.isPending ? 'Putting it back...' : 'Put this booking back'}
            </button>
          )}

          {editing && existing?.status !== 'Cancelled' && (
            <button
              type="button"
              onClick={() => setConfirmingCancel(true)}
              disabled={cancel.isPending}
              className="rounded-lg border border-crit px-3 py-2 text-[13px] font-semibold text-crit transition-colors hover:bg-crit-bg disabled:opacity-60"
            >
              {cancel.isPending ? 'Cancelling...' : 'Cancel booking'}
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-line-2 px-3.5 py-2 text-[13px] font-semibold text-ink-2 hover:border-ink-3"
          >
            Close
          </button>

          <button
            type="submit"
            /*
              `!roomId` matters now that the form can be opened without one
              (D-318). Every previous way in came from tapping a slot in a
              specific room's column, so a room was always chosen and this guard
              was never needed — it would have saved a booking holding no room.
            */
            disabled={!roomId || !staffId || !startPicked || save.isPending}
            className="flex-1 rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-on-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            {save.isPending
              ? 'Saving...'
              : editing
                ? 'Save changes'
                : dancers.length === 0
                  ? 'Hold the slot'
                  : 'Book it'}
          </button>
        </footer>
          </>
        )}
      </form>
    </div>
  )
}

/** One of a row of choices, pressed or not (D-729). */
function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`wrap-anywhere rounded-full border px-3 py-1.5 text-[12.5px] font-semibold ${
        on ? 'border-accent bg-accent-soft' : 'border-line-2 bg-surface'
      }`}
    >
      {children}
    </button>
  )
}
